import messages from './DisplayTable.messages';
import styles from './DisplayTable.module.css';
import { FormattedMessage } from 'react-intl';
import { FormattedCurrency } from '../../../../../iwIntlProvider/FormattedCurrency';
import { Rate, VariableRate } from '../../../../ui/rate';
import { DisplayTablePending } from './displayTablePending/DisplayTablePending';
import { Tooltip } from '../../../../ui/tooltip';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { Button, Icon, Modal } from '@iwoca/orion';
import { useGetDrawdownSchedules } from '../useGetDrawdownSchedules';
import { FormalOffers } from '../../../../myOffers/MyOffers.types';
import { useState } from 'react';
import RepaymentScheduleModal from '../../repaymentScheduleModal/RepaymentScheduleModal';
import { tracking } from '@iwoca/frontend-tracking-library';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';

export const DisplayTable = ({
    setSelectedOfferId,
    formalOffers,
    drawdownAmount
}: {
    drawdownAmount: number;
    formalOffers: FormalOffers;
    setSelectedOfferId: React.Dispatch<React.SetStateAction<string | null>>;
    selectedOfferId: string | null;
}) => {
    const [activeModalId, setActiveModalId] = useState<string | null>(null);

    const handleModalClose = () => {
        setActiveModalId(null);
    };

    const drawdownSchedules = useGetDrawdownSchedules(
        formalOffers,
        drawdownAmount
    );

    const isLoading = drawdownSchedules.some((schedule) => schedule.isLoading);

    if (isLoading) {
        return <DisplayTablePending />;
    }
    const isError = drawdownSchedules.find((schedule) => schedule.isError);

    if (isError) {
        return <FallbackErrorModal />;
    }

    return (
        <>
            <table
                className={styles.displayTable}
                data-testid="loan-builder-table"
            >
                <tbody>
                    <tr>
                        <td className={styles.tableHeader}>
                            <FormattedMessage {...messages.durationLabel} />
                        </td>

                        {drawdownSchedules!.map((schedule) => (
                            <td
                                key={schedule.data?.offerId}
                                className={styles.tableHeader}
                            >
                                <FormattedMessage
                                    {...messages.duration}
                                    values={{
                                        duration: schedule.data?.duration
                                    }}
                                />
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td className={styles.tableData}>
                            <FormattedMessage {...messages.upfrontFeeLabel} />
                        </td>
                        {drawdownSchedules.map((schedule) => (
                            <td
                                key={schedule.data?.offerId}
                                className={styles.tableData}
                                data-testid={`fees-${schedule.data?.duration}`}
                            >
                                <VariableRate
                                    value={schedule.data?.drawdownFee || 0}
                                    maximumFractionDigits={0}
                                    minimumFractionDigits={0}
                                />
                            </td>
                        ))}
                    </tr>

                    <tr>
                        <td className={styles.tableData}>
                            <FormattedMessage {...messages.interestRateLabel} />
                        </td>
                        {drawdownSchedules.map((schedule) => (
                            <td
                                key={schedule.data?.offerId}
                                className={styles.tableData}
                                data-testid={`interest-rate-${schedule.data?.duration}`}
                            >
                                <Rate value={schedule.data?.interestRate!} />
                            </td>
                        ))}
                    </tr>

                    <tr>
                        <td className={styles.tableData}>
                            <FormattedMessage
                                {...messages.totalRepaymentLabel}
                                values={{
                                    tooltip: (
                                        <Tooltip
                                            className={
                                                styles.selectLabelTooltip
                                            }
                                            message={
                                                <FormattedMessage
                                                    {...messages.totalRepaymentTooltip}
                                                />
                                            }
                                        />
                                    )
                                }}
                            />
                        </td>
                        {drawdownSchedules.map((schedule) => (
                            <td
                                key={schedule.data?.offerId}
                                className={styles.tableData}
                                data-testid={`total-repayment-${schedule.data?.duration}`}
                            >
                                <FormattedCurrency
                                    value={
                                        schedule.data?.totalRepayment as number
                                    }
                                />
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td className={styles.tableData}>
                            <FormattedMessage
                                {...messages.monthlyRepaymentsLabel}
                            />
                        </td>
                        {drawdownSchedules!.map((schedule) => (
                            <td
                                key={schedule.data?.offerId}
                                className={styles.tableData}
                                data-testid={`monthly-repayments-${schedule.data?.duration}`}
                            >
                                <FormattedCurrency
                                    value={
                                        schedule.data?.monthlyRepayments || 0
                                    }
                                />
                                <Button
                                    size="small"
                                    variant="tertiary"
                                    className={styles.repaymentScheduleLink}
                                    onClick={() => {
                                        setActiveModalId(
                                            schedule.data?.offerId!
                                        );
                                    }}
                                    data-testid={`repayment-schedule-${schedule.data?.duration}`}
                                >
                                    <FormattedMessage
                                        {...messages.repaymentScheduleLabel}
                                    />
                                    <Icon icon="angleRight" />
                                </Button>
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td className={styles.tableFooterLabel}>
                            <FormattedMessage
                                {...messages.selectLabel}
                                values={{
                                    tooltip: getCountry() === 'UK' && (
                                        <Tooltip
                                            className={
                                                styles.selectLabelTooltip
                                            }
                                            message={
                                                <FormattedMessage
                                                    {...messages.selectLabelTooltip}
                                                />
                                            }
                                        />
                                    )
                                }}
                            />
                        </td>
                        {drawdownSchedules.map((schedule) => (
                            <td
                                key={schedule.data?.offerId}
                                className={styles.tableFooterRadioBtn}
                            >
                                <label htmlFor={schedule.data?.offerId} />
                                <input
                                    type="radio"
                                    className={styles.radioBtn}
                                    value={schedule.data?.offerId}
                                    name="selected_offer"
                                    id={schedule.data?.offerId}
                                    onChange={() => {
                                        tracking.ampli.formalOfferSelected({
                                            formal_offer_id:
                                                schedule.data!.offerId
                                        });
                                        setSelectedOfferId(
                                            schedule.data!.offerId
                                        );
                                    }}
                                    data-action-name="Offer option selected"
                                />
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>

            <Modal
                isOpen={Boolean(activeModalId)}
                onClose={handleModalClose}
                closeIcon
            >
                <RepaymentScheduleModal
                    offerId={activeModalId!}
                    drawdownAmount={drawdownAmount}
                />
            </Modal>
        </>
    );
};
